import PropTypes from 'prop-types';
import React from 'react';
import { FormattedNumber as ReactFormattedNumber, IntlProvider } from 'react-intl';
import { currencyShape } from 'Constants/PropTypes';
import { DEFAULT_FALLBACK_LANGUAGE } from 'Constants/App';

function getCurrencyCode(currency) {
    if (typeof currency === 'object' && currency !== null && currency.code) {
        return currency.code;
    }
    if (typeof currency === 'string') {
        return currency;
    }
    return null;
}
function getCurrency(formattedNumber, customClass, currencyDisplay, currency, isPlainNumber) {
    const numberMatched = formattedNumber.match(/[\d]+((\.|,|’|'|\s)\d+)*/g);
    const isNumber = numberMatched !== null;
    const isNegative = isNumber && formattedNumber.includes('-');
    const number = isNumber ? (`${isNegative ? '-' : ''}${numberMatched[0]}`) : false;
    const isCurrencyCodeFirst = Number.isNaN(parseInt(formattedNumber.charAt(0), 10));
    // This is done like this to show, for non currency related numbers, a proper format that matches the currency one.
    // e.g: 2800 for english should be 2,800. If I dont pass currency to the library component, it won't format the number.
    let currencyValue = '';

    if (!isPlainNumber) {
        currencyValue = currencyDisplay === 'symbol' ? currency.symbol : getCurrencyCode(currency);
    }

    return (
        <span className={`nowrap ${customClass} price-tag bf-price-wrapper`}>
            {number ? isCurrencyCodeFirst ? `${currencyValue} ${number}` : `${number} ${currencyValue}` : null}
            {!isNumber && (<span>{formattedNumber}</span>)}
        </span>
    );
}

function getStyledCurrency(formattedNumber, customClass, currencyDisplay, currency) {
    const number = formattedNumber.match(/[\d\s]+((\.|,|’|')\d+)*/g)[0];
    const isNegative = formattedNumber.includes('-');
    const decimal = number.slice(-2);
    const integer = `${isNegative ? '-' : ''}${number.substr(0, number.length - 2)}`;
    const currencyCode = getCurrencyCode(currency);

    // currency code first
    if (Number.isNaN(parseInt(formattedNumber.charAt(0), 10)) && currencyCode !== 'pts') {
        return (
            <span className={`formatted-number nowrap price-tag ${customClass} bf-price-wrapper`}>
                <span className="currency-symbol">
                    {currencyDisplay === 'symbol' ? currency.symbol : currencyCode}
                </span>
                <span className="number">
                    {integer}
                    <span className="decimal">
                        {decimal}
                    </span>
                </span>
            </span>
        );
    }
    return (
        <span className={`formatted-number nowrap price-tag ${customClass} bf-price-wrapper`}>
            <span className="number">
                {integer}
                <span className="decimal">
                    {decimal}
                </span>
            </span>
            {' '}
            <span className="currency-symbol">
                {currencyDisplay === 'symbol' ? currency.symbol : currencyCode}
            </span>
        </span>
    );
}

function getPercent(formattedNumber, customClass) {
    return (
        <span className={`nowrap price-tag ${customClass}`}>
            {`${formattedNumber} %`}
        </span>
    );
}

/**
 * An internal state was implemented to force a re-rendering of the component on the client side
 * due to a bug in the Intl FormattedNumber component which returns a format on the server and a
 * different one on the client side.
 */
class FormattedNumber extends React.Component {
    constructor(props) {
        super(props);

        this.setKey = this.setKey.bind(this);
        this.getConfig = this.getConfig.bind(this);
        this.state = { key: 'server' };
    }

    componentDidMount() {
        this.setKey('client');
    }

    setKey(key) {
        this.setState({ key });
    }

    getConfig() {
        const { value, currency, percent, fractionDigits, currencyDisplay, isPlainNumber } = this.props;
        const config = { value };
        const currencyCode = getCurrencyCode(currency);

        if (currency) {
            config.maximumFractionDigits = !isPlainNumber ? fractionDigits : 0;
            config.minimumFractionDigits = !isPlainNumber ? fractionDigits : 0;
            config.style = 'currency';
            config.currency = currencyCode;
            config.currencyDisplay = currencyDisplay;
        } else if (percent) {
            config.maximumFractionDigits = 0;
            config.value = Math.floor(config.value);
        }

        return config;
    }

    render() {
        const {
            currency, percent, styled, customClass, currencyDisplay,
            isPlainNumber, hidePrice, language,
        } = this.props;
        const { key } = this.state;
        const config = this.getConfig();

        if (hidePrice) {
            return null;
        }

        return (
            <IntlProvider locale={language}>
                <ReactFormattedNumber key={key} {...config}>
                    {formattedNumber => (
                        <>
                            {currency && !styled
                                ? getCurrency(formattedNumber, customClass, currencyDisplay, currency, isPlainNumber) : null}
                            {currency && styled
                                ? getStyledCurrency(formattedNumber, customClass, currencyDisplay, currency) : null}
                            {percent
                                ? getPercent(formattedNumber, customClass) : null}
                        </>
                    )}
                </ReactFormattedNumber>
            </IntlProvider>
        );
    }
}

FormattedNumber.defaultProps = {
    currency: null,
    currencyDisplay: null,
    percent: null,
    styled: null,
    customClass: '',
    fractionDigits: 2,
    isPlainNumber: false,
    hidePrice: false,
    language: DEFAULT_FALLBACK_LANGUAGE,
};

FormattedNumber.propTypes = {
    value: PropTypes.number.isRequired,
    currency: PropTypes.oneOfType([ PropTypes.shape(currencyShape), PropTypes.string ]),
    currencyDisplay: PropTypes.string,
    percent: PropTypes.bool,
    styled: PropTypes.bool,
    isPlainNumber: PropTypes.bool,
    customClass: PropTypes.string,
    fractionDigits: PropTypes.number,
    hidePrice: PropTypes.bool,
    language: PropTypes.string,
};

export default FormattedNumber;
